import { useState } from 'react';
import { ApiError } from '../api/client';
import { useDecideRetailerKyc, useRetailerKycDocuments } from '../api/hooks';
import type { RetailerDetail } from '../api/types';
import { Alert, Button, Chip, Field, Modal, type Tone } from '../ui';
import { approvalWarning, kycWords, reviewLine, type KycWords } from './kyc-view';

/**
 * A-04 — Admin's decision on a retailer's KYC pack (CR-57 P3 / D105).
 *
 * D105 supersedes D94: KYC gates the ORDER, not the account. The retailer files their own pack after
 * signing in (R-18); this is where Admin says yes or no, and until it says yes they cannot submit a
 * PO. **No new page and no new permission** — A-04 already shows the pack, and `retailer.approve` is
 * the code that already covers deciding an application (D90/D91 are splitting `order.approve`, and a
 * new code here would collide with that work).
 *
 * The decision is ONE for the whole pack, with a reason, not one per document (D105 Q3). Approving
 * with nothing on file is allowed (Q1) and the screen says plainly that that is what is being done.
 * Rejecting an APPROVED account revokes it (Q4) — and says what that does NOT do, because what
 * becomes of orders already in flight is an open decision this change does not take.
 *
 * Internal screen: literal strings, not `t()` (the ESLint split runs the other way for R-18).
 *
 * Everyone who can open the account sees the state and who decided it — "who verified this business,
 * and when" is a question a year from now, and the account record should be able to answer it. Only
 * `retailer.approve` is given the buttons; the server checks it again either way.
 */
const TONE: Record<KycWords, Tone> = {
  Approved: 'done', Rejected: 'stop', 'Awaiting review': 'run', 'Nothing submitted': 'lapse',
};

export function KycDecision({ retailer: r, canDecide }: { retailer: RetailerDetail; canDecide: boolean }) {
  // The same read the evidence table below uses, so the count and the table cannot disagree.
  const pack = useRetailerKycDocuments(r.id);
  const decide = useDecideRetailerKyc(r.id);
  const [dialog, setDialog] = useState<'approve' | 'reject' | null>(null);
  const [reason, setReason] = useState('');
  const [problem, setProblem] = useState<string | null>(null);

  const documents = pack.data?.length ?? 0;
  const status = r.kyc.status;
  const words = kycWords(status, documents);
  const line = reviewLine(status, r.kyc.reviewedAt, r.kyc.reviewedBy?.name ?? null, r.kyc.rejectionReason);
  const warning = approvalWarning(documents);
  const revoking = status === 'APPROVED';

  const close = () => { setDialog(null); setReason(''); };
  const fail = (e: unknown) => {
    close();
    setProblem(e instanceof ApiError || e instanceof Error ? e.message : 'That could not be done.');
  };

  return (
    <div className="mk-kyc-decision" style={{ marginBottom: 'var(--mk-space-4)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--mk-space-3)', flexWrap: 'wrap' }}>
        <b>Verification</b>
        <Chip tone={TONE[words]}>{words}</Chip>
        <span className="mk-spacer" />
        {canDecide ? (
          <>
            {status !== 'APPROVED' ? (
              <Button size="sm" disabled={decide.isPending} onClick={() => { setProblem(null); setDialog('approve'); }}>
                Approve documents
              </Button>
            ) : null}
            {status !== 'REJECTED' ? (
              <Button size="sm" tone="ghost" disabled={decide.isPending} onClick={() => { setProblem(null); setDialog('reject'); }}>
                {revoking ? 'Revoke approval…' : 'Reject…'}
              </Button>
            ) : null}
          </>
        ) : null}
      </div>

      {line ? <p className="mk-dim">{line}</p> : null}
      {r.kyc.rejectionReason && status !== 'APPROVED' ? (
        <p>
          <span className="mk-dim">Reason the retailer was given: </span>
          <span style={{ whiteSpace: 'pre-wrap' }}>{r.kyc.rejectionReason}</span>
        </p>
      ) : null}
      {problem ? <Alert tone="stop">{problem}</Alert> : null}

      {dialog === 'approve' ? (
        <Modal
          title="Approve this retailer's documents"
          onClose={close}
          footer={(
            <>
              <Button tone="ghost" onClick={close}>Cancel</Button>
              <Button
                disabled={decide.isPending}
                onClick={() => decide.mutate({ decision: 'approve' }, { onSuccess: close, onError: fail })}
              >
                {warning ? 'Approve with no documents' : 'Approve'}
              </Button>
            </>
          )}
        >
          <p>
            {r.name} will be able to submit orders. The decision is recorded in the audit log against
            your name.
          </p>
          {warning ? <Alert tone="stop">{warning}</Alert> : null}
        </Modal>
      ) : null}

      {dialog === 'reject' ? (
        <Modal
          title={revoking ? 'Revoke this approval' : 'Reject these documents'}
          onClose={close}
          footer={(
            <>
              <Button tone="ghost" onClick={close}>Cancel</Button>
              <Button
                disabled={reason.trim().length < 3 || decide.isPending}
                onClick={() => decide.mutate({ decision: 'reject', reason: reason.trim() }, { onSuccess: close, onError: fail })}
              >
                {revoking ? 'Revoke approval' : 'Reject documents'}
              </Button>
            </>
          )}
        >
          {revoking ? (
            <Alert tone="them">
              The retailer will be unable to submit new orders until they are approved again. Orders they
              have already submitted are not touched by this: what happens to them when an approval is
              revoked has not been decided yet.
            </Alert>
          ) : null}
          <Field
            label="Reason"
            hint="The retailer reads this on their Account page. Say which document is the problem and what to send instead — otherwise they send the same one again."
          >
            <textarea rows={4} maxLength={1000} value={reason} onChange={(e) => setReason(e.target.value)} />
          </Field>
        </Modal>
      ) : null}
    </div>
  );
}
